import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useApi, errMsg } from "@/lib/api";
import { fmtDT } from "@/lib/brand";
import { SecureFile } from "@/components/kyc/SecureFile";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const DOCS = [["selfie", "Live selfie"], ["dl_front", "Licence front"], ["dl_back", "Licence back"], ["aadhaar_front", "Aadhaar front"], ["aadhaar_back", "Aadhaar back"]];
const TABS = [["PENDING", "Pending"], ["APPROVED", "Approved"], ["REJECTED", "Rejected"], ["", "All"]];
export const KYC_STYLE = {
  PENDING: "bg-amber-500/15 text-amber-300", APPROVED: "bg-emerald-500/15 text-emerald-300",
  REJECTED: "bg-rose-500/15 text-rose-300", NOT_SUBMITTED: "bg-white/10 text-neutral-400",
};
export const KycBadge = ({ status, testId }) => (
  <span data-testid={testId} className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${KYC_STYLE[status] || KYC_STYLE.NOT_SUBMITTED}`}>
    {(status || "NOT_SUBMITTED").replace("_", " ").toLowerCase()}
  </span>
);

function ReviewDialog({ k, onClose, onDone }) {
  const api = useApi();
  const [reason, setReason] = useState("");
  const review = async (action) => {
    try {
      await api.patch(`/admin/kyc/${k.user_id}`, { action, reason });
      toast.success(action === "approve" ? "Customer verified" : "Verification rejected");
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto" data-testid="kyc-review-dialog">
        <DialogHeader><DialogTitle>{k.name || k.email} <KycBadge status={k.status} /></DialogTitle></DialogHeader>
        <div className="grid gap-3 text-sm sm:grid-cols-3">
          <div><p className="text-neutral-500">Email</p><p>{k.email}</p></div>
          <div><p className="text-neutral-500">Licence number</p><p className="font-mono" data-testid="kyc-review-dl">{k.dl_number}</p></div>
          <div><p className="text-neutral-500">Aadhaar</p><p className="font-mono">XXXX XXXX {k.aadhaar_last4}</p></div>
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          {DOCS.map(([key, label]) => (
            <div key={key}>
              <p className="mb-1 text-xs text-neutral-400">{label}</p>
              <SecureFile fileId={k.docs?.[key]} alt={label} testId={`kyc-review-file-${key}`} className="aspect-[4/3] w-full rounded-md" />
            </div>
          ))}
        </div>
        <p className="text-xs text-neutral-500">Submitted {fmtDT(k.submitted_at)}{k.reviewed_at && ` · Reviewed ${fmtDT(k.reviewed_at)} by ${k.reviewed_by}`}{k.reject_reason && ` · Reason: ${k.reject_reason}`}</p>
        <div className="space-y-3 rounded-md border border-white/10 p-4">
          <p className="text-sm">Check the selfie matches the licence photo, the name matches on both documents, and the licence is valid.</p>
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required to reject), e.g. Licence photo is blurry" data-testid="kyc-reject-reason"
            className="w-full rounded-md border border-white/10 bg-[#0A0A0A] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#FF5A00]" />
          <div className="flex flex-wrap gap-2">
            <button onClick={() => review("approve")} data-testid="kyc-approve-btn" className="rounded-md bg-emerald-600 px-5 py-2 text-sm font-semibold text-white">Approve</button>
            <button onClick={() => review("reject")} data-testid="kyc-reject-btn" className="rounded-md border border-rose-500/50 px-5 py-2 text-sm font-semibold text-rose-400">Reject</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminKyc() {
  const api = useApi();
  const [status, setStatus] = useState("PENDING");
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/kyc", { params: { status } }).then((r) => setList(r.data));
  }, [api, status]);
  useEffect(() => {
    load();
  }, [load]);
  return (
    <div className="space-y-6" data-testid="admin-kyc">
      <h1 className="text-3xl font-semibold tracking-tight">Verifications</h1>
      <div className="flex gap-2">
        {TABS.map(([v, l]) => (
          <button key={l} onClick={() => setStatus(v)} data-testid={`kyc-tab-${l.toLowerCase()}`}
            className={`rounded-md px-3 py-1.5 text-sm ${status === v ? "bg-[#FF5A00] text-white" : "bg-white/5 text-neutral-400 hover:text-white"}`}>{l}</button>
        ))}
      </div>
      <div className="overflow-x-auto rounded-lg border border-white/10 bg-[#141414]">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-neutral-500">
            <tr>{["Customer", "Licence no.", "Aadhaar", "Submitted", "Status"].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {list.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-neutral-500" data-testid="kyc-empty">Nothing here.</td></tr>}
            {list.map((k) => (
              <tr key={k.user_id} onClick={() => setSel(k)} className="cursor-pointer hover:bg-white/5" data-testid={`kyc-row-${k.user_id}`}>
                <td className="px-4 py-3"><p className="font-medium">{k.name}</p><p className="text-xs text-neutral-500">{k.email}</p></td>
                <td className="px-4 py-3 font-mono text-xs">{k.dl_number}</td>
                <td className="px-4 py-3 font-mono text-xs">XXXX {k.aadhaar_last4}</td>
                <td className="px-4 py-3 text-xs text-neutral-400">{fmtDT(k.submitted_at)}</td>
                <td className="px-4 py-3"><KycBadge status={k.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sel && <ReviewDialog k={sel} onClose={() => setSel(null)} onDone={() => { setSel(null); load(); }} />}
    </div>
  );
}
